/**
 * Which version of the typographic ad-card renderer made an image.
 *
 * `/api/generate-image` stamps `media.metadata.cardRenderer` when it renders a typographic card
 * (`ad_typographic_card`). Images made before the stamp existed carry none; they are version 1.
 * Bump `CURRENT_CARD_RENDERER` whenever the card renderer changes how a card looks, so a Concept
 * whose sizes were made on either side of the change can say so instead of reading "3 sizes ready".
 *
 * ITC Tax run 11: Regenerate on 1.91:1 gave a new card, while 1:1 and 4:5 kept the old flat look
 * and a "Tax" box, and the header still said "3 sizes ready".
 */
export const CURRENT_CARD_RENDERER = 'v2';

const UNMARKED_VERSION = 1;

/** The numeric version in a `cardRenderer` marker ('v2' is 2); an unmarked image is version 1. */
export function cardRendererVersion(marker: string | null | undefined): number {
  const match = typeof marker === 'string' ? /^v(\d+)$/.exec(marker.trim()) : null;
  return match ? Number(match[1]) : UNMARKED_VERSION;
}

/** Reads the marker off a media row's `metadata`, or null when the image carries none. */
export function cardRendererFromMetadata(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const marker = (metadata as { cardRenderer?: unknown }).cardRenderer;
  return typeof marker === 'string' && marker.trim() ? marker.trim() : null;
}

export interface SizeRenderer {
  variantId: string;
  aspectRatio: string;
  /** Null when the size has no image yet, so no renderer made it. */
  marker: string | null | undefined;
  hasImage: boolean;
}

/**
 * The sizes made by an OLDER renderer than the newest one in this set. Judged relative to the set,
 * never against an absolute: an image with no marker is only called older when a sibling carries a
 * newer one, because a missing marker alone does not prove a card is stale.
 */
export function sizesFromOlderRenderer(sizes: readonly SizeRenderer[]): SizeRenderer[] {
  const withImage = sizes.filter((size) => size.hasImage);
  if (withImage.length < 2) return [];
  const newest = Math.max(...withImage.map((size) => cardRendererVersion(size.marker)));
  return withImage.filter((size) => cardRendererVersion(size.marker) < newest);
}
